import React, { type Context, type FC, type PropsWithChildren } from 'react';
import { observer } from 'mobx-react';

export const createContextProvider = <
    TContext,
    TProps extends Record<string, any> = Record<string, any>,
>(
    context: Context<TContext>,
    useContextValue: (props: TProps) => TContext,
): FC<PropsWithChildren<TProps>> => {
    return props => {
        const value = useContextValue(props);

        return <context.Provider value={value}>{props.children}</context.Provider>;
    };
};

export const createObservableContextProvider = <
    TContext,
    TProps extends Record<string, any> = Record<string, any>,
>(
    context: Context<TContext>,
    useContextValue: (props: TProps) => TContext,
): FC<PropsWithChildren<TProps>> => {
    return observer(createContextProvider(context, useContextValue));
};

export const composeContextProviders = (
    ...providers: FC<PropsWithChildren>[]
): FC<PropsWithChildren> => {
    const reversedProviders = [...providers].reverse();

    return ({ children }) => {
        return (
            <>
                {reversedProviders.reduce(
                    (acc, Provider) => (
                        <Provider>{acc}</Provider>
                    ),
                    children,
                )}
            </>
        );
    };
};

export const withContextProviders = <P extends object>(
    Component: React.ComponentType<P>,
    providers: FC<PropsWithChildren>[],
): FC<P> => {
    const Providers = composeContextProviders(...providers);
    return (props: P) => (
        <Providers>
            <Component {...props} />
        </Providers>
    );
};
